import { useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import {
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  useTrackedQuery as useQuery,
  useTrackedMutation as useMutation,
} from '@ais-ui/performance-tracking';
import { useBasePath } from '@ais-ui/custom-hooks';
import {
  Configuration,
  PortApi,
  type PortExtended,
  type PortList,
  type StandardResponse,
} from '@ais-ui/api-spec/portserver';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';
import { useCredentials } from '@ais-ui/auth';

/**
 * Hook to get the PortApi client for admin operations
 */
const usePortAdminApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  const portserverApi = useMemo(() => {
    let basePath = getBasePath();
    let configuration: Configuration;

    if (hostname.includes('localhost')) {
      const username = credentials?.username || '';
      const password = credentials?.password || '';

      const authHeader = getAuthHeaderFromCredentials(username, password);
      configuration = new Configuration({
        basePath: `${basePath}api/portserver`,
        headers: {
          'X-Application-Name': 'marinepulse',
          Authorization: authHeader,
        },
      });
    } else {
      basePath = `${basePath}aisweb/api/portserver`;
      configuration = new Configuration({
        basePath: basePath,
        headers: {
          'Aisweb-Session': sessionId,
          'X-Application-Name': 'marinepulse',
        },
      });
    }

    return new PortApi(configuration);
  }, [sessionId, hostname, credentials]);

  return apiClientWrapper(portserverApi) as PortApi;
};

/**
 * Hook to get a list of all ports for admin
 */
export const usePortAdminList = (
  pattern = '',
  offset = 0,
  limit = 10000,
): UseQueryResult<Record<'records', Array<PortList>>> => {
  const portApi = usePortAdminApi();

  return useQuery(
    {
      queryKey: ['portAdminList', pattern, offset, limit],
      queryFn: async () => {
        return await portApi.portListGet({ pattern, offset, limit });
      },
    },
    'portAdmin:list',
  );
};

/**
 * Hook to get extended port information by ID
 */
export const usePortAdminGet = (
  portId: number | string | undefined,
): UseQueryResult<PortExtended> => {
  const portApi = usePortAdminApi();

  return useQuery(
    {
      queryKey: ['portAdmin', portId],
      queryFn: async () => {
        if (!portId) {
          throw new Error('Port ID is required');
        }
        return await portApi.portInfoIdGet({ id: String(portId) });
      },
      enabled: !!portId,
    },
    'portAdmin:get',
  );
};

/**
 * Hook to create a new port
 */
export const usePortCreate = (): UseMutationResult<
  PortExtended,
  Error,
  PortExtended,
  unknown
> => {
  const portApi = usePortAdminApi();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: async (portData: PortExtended) => {
        return await portApi.portInfoPost({ portExtended: portData });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['portAdminList'] });
        queryClient.invalidateQueries({ queryKey: ['portListGet'] });
      },
    },
    'portAdmin:create',
  );
};

/**
 * Hook to update an existing port
 */
export const usePortUpdate = (): UseMutationResult<
  PortExtended,
  Error,
  { id: number; portData: PortExtended },
  unknown
> => {
  const portApi = usePortAdminApi();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: async ({ id, portData }) => {
        return await portApi.portInfoIdPut({ id, portExtended: portData });
      },
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries({ queryKey: ['portAdminList'] });
        queryClient.invalidateQueries({ queryKey: ['portAdmin', id] });
        queryClient.invalidateQueries({ queryKey: ['port', id] });
        queryClient.invalidateQueries({ queryKey: ['portListGet'] });
      },
    },
    'portAdmin:update',
  );
};

/**
 * Hook to delete a port
 */
export const usePortDelete = (): UseMutationResult<
  StandardResponse,
  Error,
  number,
  unknown
> => {
  const portApi = usePortAdminApi();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: async (id: number) => {
        return await portApi.portInfoIdDelete({ id });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['portAdminList'] });
        queryClient.invalidateQueries({ queryKey: ['portListGet'] });
      },
    },
    'portAdmin:delete',
  );
};
